import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components/macro";
import { lighten, opacify} from "polished";
import { sortBy } from "lodash";
import { FormattedMessage, FormattedDate } from "react-intl";

import { Heading, Icon, Link, Button } from "../../components";

const MAX_TESTIMONIALS = 6;

const Wrapper = styled.section`
  margin-top: 1rem;

  @media (${props => props.theme.tabletScreen}) {
    margin-top: 4rem;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const HeaderCta = styled(Link)`
  font-size: ${props => props.theme.fontSizes.small};
  font-weight: bold;
  white-space: nowrap;
`;

const Box = styled.div`
  margin-top: 1rem;
  padding: 1.5rem;
  border-radius: ${props => props.theme.borderRadiusLarge};
  background: ${props =>
    `linear-gradient(135deg, #fedef762 0%, ${opacify(0.31, lighten(0.32, props.theme.orange))} 55%, ${opacify(0.24, lighten(
      0.3,
      props.theme.pink,
    ))} 100%)`};

  @media (${props => props.theme.tabletScreen}) {
    padding: 2rem;
  }
`;

const SectionFooter = styled.div`
  margin-top: 1.5rem;
  text-align: center;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.25rem;

  @media (${props => props.theme.tabletScreen}) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (${props => props.theme.desktopScreen}) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const Card = styled.article`
  position: relative;
  display: flex;
  flex-direction: column;
  background-color: ${props => props.theme.white};
  border-radius: ${props => props.theme.borderRadius};
  box-shadow: ${props => props.theme.boxShadowSmall};
  padding: 1.5rem 1.25rem 1.25rem;
  transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;

  &:hover {
    transform: translateY(-3px);
    box-shadow: ${props => props.theme.boxShadow};
  }
`;

const QuoteIcon = styled(Icon)`
  margin-bottom: 0.5rem;
  opacity: 0.85;
`;

const Quote = styled.p`
  flex: 1;
  margin: 0 0 1rem;
  color: ${props => props.theme.gray800};
  font-size: ${props => props.theme.fontSizes.default};
  line-height: 1.5;
  font-style: italic;
`;

const CardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 0.75rem;
`;

const ReviewerName = styled.strong`
  color: ${props => props.theme.dark};
  font-family: "Quicksand";
  font-size: ${props => props.theme.fontSizes.small};
`;

const ReviewDate = styled.small`
  color: ${props => props.theme.gray600};
  font-size: ${props => props.theme.fontSizes.tiny};
`;

const Testimonials = ({ provider, course }) => {
  const allTestimonials = sortBy(
    (course.ratings || []).filter(({ text }) => text && text.trim() !== ""),
    ({ createdAt }) => createdAt,
  ).reverse();

  const testimonials = allTestimonials.slice(0, MAX_TESTIMONIALS);

  if (testimonials.length <= 0) {
    return "";
  }

  const hasMore = allTestimonials.length > MAX_TESTIMONIALS;
  const publicUrl = `/public/de/providers/${provider.id}/ratings?course_id=${course.id}`;

  return (
    <Wrapper>
      <SectionHeader>
        <Heading level={3}>
          <FormattedMessage id="pages.Course.testimonialsTitle" />
        </Heading>
        {hasMore && (
          <HeaderCta href={publicUrl} target="_blank" muted>
            <FormattedMessage id="pages.Course.allRatings" />
          </HeaderCta>
        )}
      </SectionHeader>
      <Box>
        <Grid>
          {testimonials.map(({ text, createdAt, name }, index) => (
            <Card key={createdAt + index}>
              <QuoteIcon name="quote-right" color="primary" size="large" />
              <Quote>{text}</Quote>
              <CardFooter>
                <ReviewerName>{name || <FormattedMessage id="pages.Course.anonymous" />}</ReviewerName>
                <ReviewDate>
                  <FormattedDate month="short" day="2-digit" year="numeric" value={Date.parse(createdAt)} />
                </ReviewDate>
              </CardFooter>
            </Card>
          ))}
        </Grid>
        {hasMore && (
          <SectionFooter>
            <Button to={publicUrl} target="_blank" color="white">
              <FormattedMessage id="pages.Course.allRatings" />
            </Button>
          </SectionFooter>
        )}
      </Box>
    </Wrapper>
  );
};

Testimonials.propTypes = {
  provider: PropTypes.object.isRequired,
  course: PropTypes.object.isRequired,
};

export default Testimonials;
